import React, { useState, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import { Icon } from 'components/patterns/Icon/Icon'
import { findMostRecentRevision } from 'utils/revisionHelpers'
import { isVariantCompatible } from 'utils/feature'
import { MAX_CAN_INPUTS, MAX_SWITCH_INDICATORS } from 'utils/constants'
import {
  MAX_NUMBER_LOGICS,
  MAX_NUMBER_TIMERS,
} from '../AddRemoveVehicleFeatures/constants'
import displayModuleName from 'i18n/displayConstants/displayModuleName'

import FeatureVersionCard from './FeatureVersionCard'
import {
  Scrim,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  MainContent,
  ContentContainer,
  ExplainerText,
  SideContainer,
  SideTab,
  SideTabAccent,
  SideTabContent,
  SideTabFeatureNumber,
  SideTabStatusRow,
  SideTabStatusText,
  ModalFooter,
  FooterButton,
} from './FeatureVersionUpdateModal.style'

/**
 * Check if updating a feature would exceed ECU resource limits.
 * Returns { isOver: false } if within limits, or
 * { isOver: true, reasons: [...] } with detailed explanation.
 */
const pluralize = (count, singular, plural) => (count === 1 ? singular : plural)

const checkResourceLimits = (
  currentFeature,
  updatedFeatureData,
  onGoingSet,
  vehicleECUs
) => {
  if (!onGoingSet || !currentFeature.moduleName) {
    return { isOver: false }
  }

  const moduleName = currentFeature.moduleName
  const moduleDisplayName = displayModuleName(moduleName)

  // Get per-module max values from vehicle ECU data
  const matchingEcu =
    vehicleECUs && vehicleECUs.find((ecu) => ecu.id === moduleName)
  const maxLogic = (matchingEcu && matchingEcu.maxLogic) || MAX_NUMBER_LOGICS
  const maxTimer = (matchingEcu && matchingEcu.maxTimer) || MAX_NUMBER_TIMERS
  const maxCanInput =
    (matchingEcu && matchingEcu.maxCanInputs) || MAX_CAN_INPUTS
  const maxSwitchIndicator =
    (matchingEcu && matchingEcu.switchIndicatorMax) || MAX_SWITCH_INDICATORS

  // Compute current totals for this module from the onGoingSet
  const moduleFeaturesOnVehicle = onGoingSet.filter(
    (f) => f.moduleName === moduleName && !f.isMarkedForDeletion
  )

  const currentLogicTotal = moduleFeaturesOnVehicle.reduce(
    (sum, f) => sum + (f.complexQueryCount || 0),
    0
  )
  const currentTimerTotal = moduleFeaturesOnVehicle.reduce(
    (sum, f) => sum + (f.timerQueryCount || 0),
    0
  )
  const currentCanInputs = new Set(
    moduleFeaturesOnVehicle.flatMap((f) => f.canInputs || [])
  )
  const currentSwitchIndicators = new Set(
    moduleFeaturesOnVehicle.flatMap((f) => f.switchIndicators || [])
  )

  // Compute deltas
  const updatedLogic = updatedFeatureData.complexQueryCount
  const updatedTimer = updatedFeatureData.timerQueryCount
  const updatedCanInputs = updatedFeatureData.canInputs || null
  const updatedSwitchIndicators = updatedFeatureData.switchIndicators || null

  const currentFeatureLogic = currentFeature.complexQueryCount || 0
  const currentFeatureTimer = currentFeature.timerQueryCount || 0
  const currentFeatureCanInputs = currentFeature.canInputs || []

  // Check each resource type independently based on data availability
  const hasLogicData = updatedLogic != null
  const hasTimerData = updatedTimer != null
  const hasCanInputData = updatedCanInputs != null
  const hasSwitchIndicatorData = updatedSwitchIndicators != null

  // If no resource data at all, skip the check
  if (
    !hasLogicData &&
    !hasTimerData &&
    !hasCanInputData &&
    !hasSwitchIndicatorData
  ) {
    return { isOver: false }
  }

  let isOverLogic = false
  let isOverTimer = false
  let isOverCanInputs = false
  let isOverSwitchIndicators = false
  let newLogicTotal = currentLogicTotal
  let newTimerTotal = currentTimerTotal
  let newCanInputTotal = currentCanInputs.size
  let newSwitchIndicatorTotal = currentSwitchIndicators.size

  if (hasLogicData) {
    const logicDelta = (updatedLogic || 0) - currentFeatureLogic
    newLogicTotal = currentLogicTotal + logicDelta
    isOverLogic = newLogicTotal > maxLogic
  }

  if (hasTimerData) {
    const timerDelta = (updatedTimer || 0) - currentFeatureTimer
    newTimerTotal = currentTimerTotal + timerDelta
    isOverTimer = newTimerTotal > maxTimer
  }

  if (hasCanInputData) {
    // For CAN inputs: remove current feature's CAN inputs, add updated feature's
    const canInputsAfterUpdate = new Set(currentCanInputs)
    currentFeatureCanInputs.forEach((ci) => canInputsAfterUpdate.delete(ci))
    updatedCanInputs.forEach((ci) => canInputsAfterUpdate.add(ci))
    newCanInputTotal = canInputsAfterUpdate.size
    isOverCanInputs = newCanInputTotal > maxCanInput
  }

  if (hasSwitchIndicatorData) {
    // Rebuild set from all module features EXCLUDING the current feature,
    // then add the updated feature's indicators. This correctly preserves
    // shared indicators that other features still reference.
    const switchIndicatorsAfterUpdate = new Set(
      moduleFeaturesOnVehicle
        .filter((f) => f.id !== currentFeature.id)
        .flatMap((f) => f.switchIndicators || [])
    )
    updatedSwitchIndicators.forEach((si) => switchIndicatorsAfterUpdate.add(si))
    newSwitchIndicatorTotal = switchIndicatorsAfterUpdate.size
    isOverSwitchIndicators = newSwitchIndicatorTotal > maxSwitchIndicator
  }

  if (
    !isOverLogic &&
    !isOverTimer &&
    !isOverCanInputs &&
    !isOverSwitchIndicators
  ) {
    return { isOver: false }
  }

  // Build structured data for rendering
  const exceededResources = []
  const currentTotals = []
  const removals = []

  if (isOverLogic) {
    exceededResources.push({ max: maxLogic, label: 'Logic Sets' })
    currentTotals.push({
      count: newLogicTotal,
      label: 'Logic Sets',
      module: moduleDisplayName,
    })
    removals.push({
      count: newLogicTotal - maxLogic,
      label: pluralize(newLogicTotal - maxLogic, 'Logic Set', 'Logic Sets'),
      module: moduleDisplayName,
    })
  }
  if (isOverTimer) {
    exceededResources.push({ max: maxTimer, label: 'Timers' })
    currentTotals.push({
      count: newTimerTotal,
      label: 'Timers',
      module: moduleDisplayName,
    })
    removals.push({
      count: newTimerTotal - maxTimer,
      label: pluralize(newTimerTotal - maxTimer, 'Timer', 'Timers'),
      module: moduleDisplayName,
    })
  }
  if (isOverCanInputs) {
    exceededResources.push({ max: maxCanInput, label: 'CAN Inputs' })
    currentTotals.push({
      count: newCanInputTotal,
      label: 'CAN Inputs',
      module: moduleDisplayName,
    })
    removals.push({
      count: newCanInputTotal - maxCanInput,
      label: pluralize(
        newCanInputTotal - maxCanInput,
        'CAN Input',
        'CAN Inputs'
      ),
      module: moduleDisplayName,
    })
  }
  if (isOverSwitchIndicators) {
    exceededResources.push({
      max: maxSwitchIndicator,
      label: 'Switch Indicators',
    })
    currentTotals.push({
      count: newSwitchIndicatorTotal,
      label: 'Switch Indicators',
      module: moduleDisplayName,
    })
    removals.push({
      count: newSwitchIndicatorTotal - maxSwitchIndicator,
      label: pluralize(
        newSwitchIndicatorTotal - maxSwitchIndicator,
        'Switch Indicator',
        'Switch Indicators'
      ),
      module: moduleDisplayName,
    })
  }

  return { isOver: true, exceededResources, currentTotals, removals }
}

const ALLOWED_REVISION_STATUSES = ['active', 'test']

/**
 * Determines whether a feature has a valid update available.
 * Returns the enriched updated-feature object, or null when no eligible
 * replacement exists (e.g. the only newer revisions are "New" status).
 *
 * Exported so the parent component can use the same logic to decide
 * whether to show the "Update Feature Versions" button and which
 * features to pass into the modal.
 */
// eslint-disable-next-line import/no-unused-modules
export const getUpdatedFeature = (
  feature,
  vehicleECUs,
  onGoingSet,
  serialNumber
) => {
  // Case 1: Feature has revisions array with a newer revision
  if (
    feature.isFeatureLatestRevision === false &&
    feature.revisions &&
    feature.revisions.length > 0
  ) {
    const currentRevision = feature.revision || '-'
    // Only consider revisions with Test or Active status (exclude New)
    const eligibleRevisions = feature.revisions.filter(
      (rev) =>
        rev.status &&
        ALLOWED_REVISION_STATUSES.includes(rev.status.toLowerCase())
    )
    const newestRevision = findMostRecentRevision(eligibleRevisions)

    if (newestRevision) {
      const newestRev = newestRevision.revision || newestRevision.release || '-'
      // Only offer the update if the newest revision is different from the current one
      if (newestRev !== currentRevision) {
        // Check ECU app version compatibility
        const moduleName = newestRevision.moduleName || feature.moduleName
        const revisionMinVariant = newestRevision.minCompatibleVariant
        const matchingEcu =
          vehicleECUs && vehicleECUs.find((ecu) => ecu.id === moduleName)
        const ecuVariant = matchingEcu && matchingEcu.ecuVariant
        const compatible = isVariantCompatible(ecuVariant, revisionMinVariant)

        // Check resource limits
        const resourceCheck = checkResourceLimits(
          feature,
          newestRevision,
          onGoingSet,
          vehicleECUs
        )

        const allReasons = []
        if (!compatible) {
          allReasons.push(
            `CHEC cannot update to the newer version of this Feature because it is incompatible with the ECU's software version.`
          )
          allReasons.push(` `)
          allReasons.push(
            `To commit your changes to ${
              serialNumber || 'this vehicle'
            }, go back and edit this Feature to modify its minimum Diagnostic Variant.`
          )
        }

        const isFullyCompatible = compatible && !resourceCheck.isOver

        return {
          ...newestRevision,
          revision: newestRev,
          partNumber: newestRevision.partNumber || feature.partNumber,
          isCompatible: isFullyCompatible,
          isRevisionUpdate: true,
          incompatibilityReason: allReasons,
          resourceLimitData: resourceCheck.isOver ? resourceCheck : null,
        }
      }
    }
  }

  // Case 2: Feature has a newer version via latestSnapshot (not a revision update)
  if (
    feature.latestOnVehicle === false &&
    feature.latestSnapshot?.object?.feature
  ) {
    const updatedFeature = feature.latestSnapshot.object.feature
    // Only allow updates to features with Test or Active status
    if (
      updatedFeature.status &&
      !ALLOWED_REVISION_STATUSES.includes(updatedFeature.status.toLowerCase())
    ) {
      return null
    }
    const moduleName = updatedFeature.moduleName || feature.moduleName
    const featureMinVariant = updatedFeature.minCompatibleVariant
    const matchingEcu =
      vehicleECUs && vehicleECUs.find((ecu) => ecu.id === moduleName)
    const ecuVariant = matchingEcu && matchingEcu.ecuVariant
    const compatible = isVariantCompatible(ecuVariant, featureMinVariant)

    // Check resource limits
    const resourceCheck = checkResourceLimits(
      feature,
      updatedFeature,
      onGoingSet,
      vehicleECUs
    )

    const allReasons = []
    if (!compatible) {
      allReasons.push(
        `CHEC cannot update to the newer version of this Feature because it is incompatible with the ECU's software version.`
      )
      allReasons.push(` `)
      allReasons.push(
        `To commit your changes to ${
          serialNumber || 'this vehicle'
        }, go back and edit this Feature to modify its minimum Diagnostic Variant.`
      )
    }

    const isFullyCompatible = compatible && !resourceCheck.isOver

    return {
      ...updatedFeature,
      isCompatible: isFullyCompatible,
      incompatibilityReason: allReasons,
      resourceLimitData: resourceCheck.isOver ? resourceCheck : null,
    }
  }

  return null
}

const getSideTabStatus = (
  selection,
  isIncompatible,
  noReplacement,
  updatedFeatureStatus,
  revisionChanged
) => {
  if (selection === 'update') {
    return { text: 'Update to new version', icon: 'success' }
  }
  if (selection === 'keep') {
    return { text: 'Keep current version', icon: 'success' }
  }

  if (revisionChanged) {
    return { text: 'Update available', icon: 'warning' }
  }
  return { text: 'Update recommended', icon: 'warning' }
}

const FeatureVersionUpdateModal = ({
  visible,
  features,
  serialNumber,
  vehicleECUs,
  onGoingSet,
  onClose,
  onCommit,
}) => {
  // Filter out features whose only newer revisions have ineligible statuses
  // (e.g. "New") so they never appear in the modal, and deduplicate by id.
  // ALWAYS include obsolete features even if they have no replacement available.
  const featureList = useMemo(() => {
    const filtered = (features || []).filter((feat) => {
      // Always include obsolete features (they show with 'keep only' / disabled update)
      if (feat.status && feat.status.toLowerCase() === 'obsolete') return true
      // Include features with valid updates
      return (
        getUpdatedFeature(feat, vehicleECUs, onGoingSet, serialNumber) !== null
      )
    })
    const seen = new Set()
    return filtered.filter((feat) => {
      if (seen.has(feat.id)) return false
      seen.add(feat.id)
      return true
    })
  }, [features, vehicleECUs, onGoingSet, serialNumber])

  const [selectedFeatureId, setSelectedFeatureId] = useState(
    featureList?.[0]?.id || null
  )

  // Keep selectedFeatureId in sync when featureList changes
  useEffect(() => {
    if (
      featureList.length > 0 &&
      !featureList.some((f) => f.id === selectedFeatureId)
    ) {
      setSelectedFeatureId(featureList[0].id)
    }
  }, [featureList, selectedFeatureId])

  const [selections, setSelections] = useState({})

  const handleSelect = useCallback((featureId, choice) => {
    setSelections((prev) => ({ ...prev, [featureId]: choice }))
  }, [])

  const allSelectionsComplete = useMemo(
    () =>
      featureList.length > 0 &&
      featureList.every((feat) => {
        const updated = getUpdatedFeature(
          feat,
          vehicleECUs,
          onGoingSet,
          serialNumber
        )
        const noReplacement = !updated
        // Features without a replacement only allow "keep" – require that selection
        if (noReplacement) return selections[feat.id] === 'keep'
        const isIncompatible = updated && updated.isCompatible === false
        // Incompatible features only allow "keep" – require that selection
        if (isIncompatible) return selections[feat.id] === 'keep'
        // All other features require any selection
        return selections[feat.id] != null
      }),
    [featureList, selections, vehicleECUs, onGoingSet, serialNumber]
  )

  const handleCommit = useCallback(() => {
    const commitSelections = featureList
      .filter((feat) => {
        const updated = getUpdatedFeature(
          feat,
          vehicleECUs,
          onGoingSet,
          serialNumber
        )
        const isIncompatible = updated && updated.isCompatible === false
        const noReplacement = !updated
        // Include all compatible features that have a selection
        return !isIncompatible && !noReplacement && selections[feat.id] != null
      })
      .map((feat) => ({
        currentFeature: feat,
        updatedFeature: getUpdatedFeature(
          feat,
          vehicleECUs,
          onGoingSet,
          serialNumber
        ),
        selection: selections[feat.id],
      }))
    onCommit(commitSelections)
  }, [featureList, selections, onCommit, vehicleECUs, onGoingSet, serialNumber])

  if (!visible || featureList.length === 0) return null

  const selectedFeature = featureList.find(
    (feat) => feat.id === selectedFeatureId
  )

  return (
    <Scrim
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <ModalTitle>{strings.feature_version_updates}</ModalTitle>
          <CloseButton onClick={onClose}>
            <Icon icon="cancel" width={14} height={14} />
          </CloseButton>
        </ModalHeader>

        {/* Body */}
        <ModalBody>
          {/* Main content (left) */}
          <MainContent>
            <ContentContainer>
              <ExplainerText>
                <p style={{ margin: 0 }}>
                  This vehicle no longer has the most recent versions of one or
                  more Features. You can continue to use the Features currently
                  on the vehicle, or you can update them. CHEC recommends that
                  you always use the most recent version of a Feature.
                </p>
                <p style={{ margin: '18px 0 0' }}>
                  Select which version of each available Feature to use on your
                  vehicle.
                </p>
              </ExplainerText>

              {selectedFeature && (
                <FeatureVersionCard
                  key={selectedFeature.id}
                  currentFeature={selectedFeature}
                  updatedFeature={getUpdatedFeature(
                    selectedFeature,
                    vehicleECUs,
                    onGoingSet,
                    serialNumber
                  )}
                  selection={selections[selectedFeature.id] || null}
                  serialNumber={serialNumber}
                  onSelect={(choice) =>
                    handleSelect(selectedFeature.id, choice)
                  }
                />
              )}
            </ContentContainer>
          </MainContent>

          {/* Side container (right) */}
          <SideContainer>
            {featureList.map((feat) => {
              const updated = getUpdatedFeature(
                feat,
                vehicleECUs,
                onGoingSet,
                serialNumber
              )
              const isIncompatible = updated && updated.isCompatible === false
              const noReplacement = !updated
              const selection = selections[feat.id]
              const isSelected = feat.id === selectedFeatureId
              const currentRevision = feat.revision || '-'
              const updatedRevision = updated ? updated.revision || '-' : '-'
              const revisionChanged = updated
                ? updatedRevision !== currentRevision
                : false
              const { text, icon } = getSideTabStatus(
                selection,
                isIncompatible,
                noReplacement,
                updated && updated.status,
                revisionChanged
              )

              return (
                <SideTab
                  key={feat.id}
                  isSelected={isSelected}
                  onClick={() => setSelectedFeatureId(feat.id)}
                >
                  <SideTabAccent isSelected={isSelected} />
                  <SideTabContent>
                    <SideTabFeatureNumber>
                      {feat.partNumber}
                    </SideTabFeatureNumber>
                    <SideTabStatusRow>
                      {icon === 'success' ? (
                        <Icon
                          icon="check-mark-only"
                          width={16}
                          height={16}
                          fill="#30a621"
                        />
                      ) : (
                        <Icon
                          icon="alert_exclamation_triangle_solid"
                          width={18}
                          height={18}
                          fill="#ffc627"
                        />
                      )}
                      <SideTabStatusText>{text}</SideTabStatusText>
                    </SideTabStatusRow>
                  </SideTabContent>
                </SideTab>
              )
            })}
          </SideContainer>
        </ModalBody>

        {/* Footer */}
        <ModalFooter>
          <FooterButton variant="secondary" onClick={onClose}>
            {strings.cancel}
          </FooterButton>
          <FooterButton
            variant="primary"
            onClick={handleCommit}
            disabled={!allSelectionsComplete}
          >
            {strings.commit_changes}
          </FooterButton>
        </ModalFooter>
      </ModalContainer>
    </Scrim>
  )
}

FeatureVersionUpdateModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  features: PropTypes.array.isRequired,
  serialNumber: PropTypes.string,
  vehicleECUs: PropTypes.array,
  onGoingSet: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
}

export default FeatureVersionUpdateModal
